import React, { FunctionComponent, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumb, Button, Dropdown, Icon, Pill, Radio } from 'rs-emd-ui-atoms';
import { getCookie, getLabel } from '../../..';
import { ILabel } from '../../../models/common/label.model';
import { ILink } from '../../../models/common/link.model';
import { ISearchSort } from '../../../models/plp/input/search-selector.model';
import { IPLPCategoryItem } from '../../../models/plp/plp-category-item.model';
import { IPLPSummary } from '../../../models/plp/plp-summary.model';
import { IPLPService } from '../../../services/plp/plp-service';
import { PageSizeControls } from '../../shared';
import { IPagination } from '../../shared/page-controls/pagination.model';
import styles from './title-area-component.module.scss';

type TitleAreaProps = {
  breadcrumb: ILink[];
  title: string;
  description: string;
  labels?: ILabel[];
  pagination: IPagination;
  summary?: IPLPSummary | null;
  sort?: ISearchSort;
  hiddenFilters: boolean;
  categoriesArea: IPLPCategoryItem[];
  toggleFilerModal: () => void;
  toggleFiltersVisbility: () => void;
  updateProducts: (pagination: IPagination, sort?: ISearchSort) => void;
  getSRPFamilyPageUrl: (familyId?: number | string) => string;
  plpService: IPLPService;
};

export const TitleArea: FunctionComponent<TitleAreaProps> = (props: TitleAreaProps) => {
  const [isDescOpen, setIsDescOpen] = useState(false);
  const [isOutOfViewport, setIsOutOfViewport] = useState(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [defaultSelection, setDefaultSelection] = useState<string | undefined>(undefined);
  const [isWebview, setIsWebview] = useState(true);

  const controlsBarRef = useRef<HTMLDivElement>(null);
  const categoriesRef = useRef<HTMLDivElement>(null);
  let history = useNavigate();
  let location = useLocation();

  let showSRPUI = (props.categoriesArea?.length ?? 0) > 0 && (props.categoriesArea[0].childCategories?.length ?? 0) > 1; // show pills unless SRP returned result from one family

  useEffect(() => {
    const handleScroll = () => {
      if (controlsBarRef.current) {
        setIsOutOfViewport(
          // cannot use window.scrollY since overflow-x: hidden on the body causes it to stop working
          Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop) >
            controlsBarRef.current.offsetTop + controlsBarRef.current.clientHeight
        );
      } else if (categoriesRef.current) {
        setIsOutOfViewport(
          // cannot use window.scrollY since overflow-x: hidden on the body causes it to stop working
          Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop) >
            categoriesRef.current.offsetTop + categoriesRef.current.clientHeight
        );
      }
    };

    document.body.addEventListener('scroll', handleScroll);

    return () => {
      document.body.removeEventListener('scroll', () => handleScroll);
    };
  }, []);

  // radio button default selection, workaround for back button issue
  useEffect(() => {
    setDefaultSelection((initialState) => {
      return initialState === '' ? undefined : '';
    });
  }, [location]);

  useLayoutEffect(() => {
    // webview set in cookie?
    setIsWebview(Boolean(JSON.parse(getCookie('webview') ?? '0'))); // will hide the header
  }, []);

  let filterToggleButton = (
    <Button
      buttonType='link'
      text={getLabel(props.labels, props.hiddenFilters ? 'show_filters' : 'hide_filters')}
      icon={props.hiddenFilters ? 'chev-right' : 'chev-left'}
      className={styles['filter-toggle']}
      onClick={props.toggleFiltersVisbility}></Button>
  );

  return (
    <div className={`${styles['title-area-container']} ${props.categoriesArea.length > 0 ? styles.srp : ''}`}>
      <div className={`inner-page-wrapper ${styles.header}`}>
        {!isWebview && props.breadcrumb.length > 0 && <Breadcrumb links={props.breadcrumb} className={styles.breadcrumb} />}
        <h1 className={styles.title}>{props.title}</h1>
        {showSRPUI && getSRPSelectedCategoryPill()}
        {getDescription()}
      </div>
      {showSRPUI && (
        <>
          <div className={styles.categories} ref={categoriesRef}>
            {getSRPCategories()}
          </div>
          {getFixedSRPHeader()}
        </>
      )}
      {/* view, sort, filter */}
      <div className={`${styles['controls-area']} ${props.categoriesArea.length > 0 ? 'inner-page-wrapper' : ''}`}>
        <div className={styles.heading}>
          <div className={`${styles['filter-toggle-wrapper']} ${props.hiddenFilters ? styles.hidden : ''}`}>{filterToggleButton}</div>
          <p
            className={`snippet ${styles['page-info']}`}
            dangerouslySetInnerHTML={{
              __html: getLabel(props.labels, 'results_counter')
                .replace('[page]', `<b>${getProductCountString()}</b>`)
                .replace('[total]', `<b>${props.summary?.totalProducts}</b>`),
            }}></p>
        </div>
        <div className={styles.controls}>
          {/* sort controls & filter toggle */}
          <div>
            {/* sort controls & filter toggle */}
            <div className={styles['sort-controls']}>
              <label>{getLabel(props.labels, 'sort_by')}</label>
              <Radio
                spacing={0.8}
                name='sort-mode'
                defaultSelection={
                  props.sort?.sortField === 'price' ? (props.sort?.direction === 0 ? 'price_desc' : 'price_asc') : 'relevance'
                }
                list={[
                  { key: 'relevance', elt: getLabel(props.labels, 'sort_by_relevance') },
                  { key: 'price_asc', elt: getLabel(props.labels, 'sort_by_price_asc') },
                  { key: 'price_desc', elt: getLabel(props.labels, 'sort_by_price_desc') },
                ]}
                selectCallback={(key) => sort(key)}></Radio>
            </div>
            {filterToggleButton}
          </div>
          {/* filter controls */}
          <Button
            className={styles['filter-control']}
            buttonType='tertiary'
            icon='filter'
            text={getLabel(props.labels, 'filters_cta')}
            onClick={props.toggleFilerModal}
          />
          {/* page size controls */}
          <div className={styles['page-controls']}>
            <PageSizeControls labels={props.labels} pagination={props.pagination} onChange={props.updateProducts} />
          </div>
          {/* mobile controls - one static and one fixed with animation */}
          {getControlsBar(false)}
          {(props.categoriesArea?.length ?? 0) === 0 && <>{getControlsBar(true)}</>}
        </div>
      </div>

      <Dropdown
        containerClassName='hide'
        isOpen={isSortDropdownOpen}
        selectCallback={(key) => sort(key)}
        onChangeState={(isOpen) => setIsSortDropdownOpen(isOpen)}
        defaultSelection='relevance'
        list={[
          { key: 'relevance', elt: <>{getLabel(props.labels, 'sort_by_relevance')}</> },
          { key: 'price_asc', elt: <>{getLabel(props.labels, 'sort_by_price_asc')}</> },
          { key: 'price_desc', elt: <>{getLabel(props.labels, 'sort_by_price_desc')}</> },
        ]}></Dropdown>
    </div>
  );

  function getDescription() {
    let description = props.description ?? '';

    let truncated = description /*.split('<')[0]*/
      .substr(0, 400);
    let isTruncated = truncated.length < description.length;

    return (
      <div className={`snippet ${styles.description}`}>
        {isTruncated && !isDescOpen && (
          <>
            <p dangerouslySetInnerHTML={{ __html: truncated + '...' }}></p>
            {!isDescOpen && (
              <button className='link' onClick={() => setIsDescOpen(true)}>
                {getLabel(props.labels, 'read_more')}
              </button>
            )}
          </>
        )}
        {(isDescOpen || !isTruncated) && (
          <>
            <div dangerouslySetInnerHTML={{ __html: description }}></div>
            {isTruncated && (
              <button className='link' onClick={() => setIsDescOpen(false)}>
                {getLabel(props.labels, 'read_less')}
              </button>
            )}
          </>
        )}
      </div>
    );
  }

  function getControlsBar(isFixed: boolean) {
    return (
      <div
        ref={!isFixed ? controlsBarRef : null}
        className={`${styles['controls-bar']}  ${isFixed ? `${styles['fixed-cta']} ${isOutOfViewport ? styles.fixed : styles.hide}` : ''}`}>
        <div className={styles.control} onClick={props.toggleFilerModal}>
          <Icon name='filter'></Icon>
          <p className={styles.label}>{getLabel(props.labels, 'filters_cta')}</p>
        </div>
        <div className={styles.vr}></div>
        <div className={styles.control} onClick={() => setIsSortDropdownOpen(true)}>
          <Icon name='sort'></Icon>
          <p className={styles.label}>{getLabel(props.labels, 'sort_cta')}</p>
        </div>
      </div>
    );
  }

  function getProductCountString() {
    let totalProducts = props.summary?.totalProducts ?? 0;
    let endCount =
      props.pagination.offset + props.pagination.size > totalProducts ? totalProducts : props.pagination.offset + props.pagination.size;

    return `${props.pagination.offset + 1}-${endCount}`;
  }

  function sort(key: string | number) {
    let sort: ISearchSort = {
      sortField: key.toString().startsWith('price') ? 'price' : 'relevance',
      direction: key === 'price_asc' ? 1 : 0,
    };

    props.updateProducts(props.pagination, sort);
  }

  // SRP methods:

  function getSRPSelectedCategoryPill(isInFixedHeader?: boolean) {
    // show pills unless SRP returned result from one family
    if (props.categoriesArea && (props.categoriesArea?.length ?? 0) === 1)
      return (
        <Pill
          color={isInFixedHeader ? 'grey' : 'white'}
          class={styles.pill}
          isClosable={true}
          closeCallback={() => navigateToSRPCategory()}>
          {getSRPCategoryName(props.categoriesArea[0])}
        </Pill>
      );
  }

  function getSRPCategories() {
    return (
      <Radio
        class={styles.options}
        spacing={0.8}
        name='cat'
        list={
          props.categoriesArea.length > 1
            ? props.categoriesArea?.map((c) => {
                //https://app.clickup.com/t/7wmc5b?comment=193595656
                if (c.childCategories?.length === 1) {
                  let childCat = c.childCategories[0];

                  return {
                    key: childCat.id,
                    elt: getSRPCategoryName(childCat),
                  };
                }
                return { key: c.id, elt: getSRPCategoryName(c) };
              })
            : props.categoriesArea[0].childCategories?.map((c) => {
                return { key: c.id, elt: getSRPCategoryName(c) };
              })
        }
        defaultSelection={defaultSelection}
        selectCallback={(key) => navigateToSRPCategory(key)}></Radio>
    );
  }

  function navigateToSRPCategory(familyId?: string | number) {
    if (familyId) {
      props.plpService.tagSRPFamilySelection(familyId);
    }
    history(props.getSRPFamilyPageUrl(familyId));
  }

  function getSRPCategoryName(category: IPLPCategoryItem) {
    return `${category.name} (${category.noOfProducts})`;
  }

  function getFixedSRPHeader() {
    return (
      <div className={`${styles['fixed-srp-header']} ${`${styles['fixed-cta']} ${isOutOfViewport ? styles.fixed : styles.hide}`}`}>
        <div className={styles.header}>
          {props.title} <span className={styles.description}>({props.description})</span>
        </div>
        {getSRPSelectedCategoryPill(true)}

        <div className={styles.categories} ref={categoriesRef}>
          {getSRPCategories()}
        </div>
      </div>
    );
  }
};
